import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "imaginary_exit_seen"; // sessionStorage — once per visit

export default function ExitIntent() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        setOpen(true);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, { email, source: "exit-intent" });
      setDone(true);
    } catch {
      toast.error(t("cart.error"));
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#111111]/60 p-6"
      onClick={() => setOpen(false)}
      data-testid="exit-intent"
    >
      <div
        className="relative w-full max-w-lg border gallery-line bg-[#faf8f5] px-8 py-10 md:px-12"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="exit-close"
          className="absolute top-4 right-4 text-[#111111]/50 hover:text-[#111111]"
        >
          <X className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#111111]">
          {t("exit.title.a")}
          <span className="italic">{t("exit.title.i")}</span>
        </h2>
        <p className="mt-4 text-sm font-light leading-relaxed text-[#111111]/65">
          {t("exit.body")}
        </p>
        {done ? (
          <div className="mt-6" data-testid="exit-code">
            <p className="text-sm font-light text-[#111111]/70">{t("exit.code")}</p>
            <p className="mt-3 inline-block border border-[#111111] bg-white px-6 py-3 font-display text-2xl tracking-[0.2em] text-[#111111]">
              FIRST10
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex" data-testid="exit-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("footer.newsletter.placeholder")}
              data-testid="exit-email"
              className="w-full border border-[#111111]/25 bg-white px-4 py-3 text-sm font-light outline-none focus:border-[#111111]"
            />
            <button
              type="submit"
              data-testid="exit-submit"
              className="shrink-0 bg-[#111111] px-5 text-sm font-light text-[#faf8f5] transition-opacity duration-300 hover:opacity-85"
            >
              {t("exit.button")}
            </button>
          </form>
        )}
        <button
          onClick={() => setOpen(false)}
          className="mt-5 text-xs font-light text-[#111111]/45 link-underline"
          data-testid="exit-dismiss"
        >
          {t("exit.noThanks")}
        </button>
      </div>
    </div>
  );
}
